/**
 * Book / publication price-option truth.
 *
 * Gift-price values ($25 / $40 / $60 / $88 / $111) belong to Digital formats only.
 * Each hardcover Format has one fixed price taken from the product's hardcover
 * variant data — never a leftover digital gift tier.
 *
 * A CSS-only hide of the gift-price picker is not enough: Shopify cart and
 * checkout still serialize the selected variant's option values. This module
 * remaps selection, hidden variant id, URL, add-to-cart payload, and cart
 * option presentation onto one truthful hardcover variant.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root) root.StarlyzedBookPriceTruth = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function factory() {
  const DIGITAL_GIFT_LABELS = Object.freeze(['$25', '$40', '$60', '$88', '$111']);
  const PRICE_OPTION = 'Your price';
  const FORMAT_OPTION = 'Format';
  const DIGITAL_FORMAT = 'Digital PDF';
  const HARDCOVER_FORMAT = 'Hardcover + PDF';
  const SYNC_FLAG = '__stzBookPriceTruthSync';
  const lastDigitalGiftByProductId = new Map();
  const PUBLICATION_CENSUS_HANDLES = Object.freeze([
    'the-book-of-you',
    'the-mirror',
    'my-chart-my-style',
    'your-sky-story',
    'starlyzed-map',
    'shadow-to-light-kit',
    'birth-chart-oracle-deck',
    'starlyzed-identity-kit',
    'personal-ritual-calendar',
    'gritwink',
    'the-natal-wardrobe-issue',
  ]);
  const DUAL_PROPERTY_CONTRACT = Object.freeze({
    status: 'posted',
    reason: 'The Book posts human-readable checkout properties alongside hidden canonical fields; theme consumers read canonical values first and fall back to legacy visible keys.',
    single: {
      visible: { 'Birth Date': 'MM/DD/YYYY', 'Birth Time': 'h:mm AM/PM' },
      hiddenCanonical: { '_Birth Date': 'YYYY-MM-DD', '_Birth Time': 'HH:MM' },
    },
    mirror: {
      status: 'designed-not-posted',
      visible: {
        'A Birth Date': 'MM/DD/YYYY',
        'A Birth Time': 'h:mm AM/PM',
        'B Birth Date': 'MM/DD/YYYY',
        'B Birth Time': 'h:mm AM/PM',
      },
      hiddenCanonical: {
        '_A Birth Date': 'YYYY-MM-DD',
        '_A Birth Time': 'HH:MM',
        '_B Birth Date': 'YYYY-MM-DD',
        '_B Birth Time': 'HH:MM',
      },
      currentPostedUntilBackendReady: {
        'A Birth Date': 'YYYY-MM-DD',
        'A Birth Time': 'HH:MM',
        'B Birth Date': 'YYYY-MM-DD',
        'B Birth Time': 'HH:MM',
      },
    },
  });

  function normalizeLabel(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function looksLikeGiftPrice(value) {
    return /^\$\d+(?:\.\d{2})?$/.test(normalizeLabel(value).replace(/\.00$/, ''));
  }

  function isGiftTierLabel(value, labels) {
    const normalized = normalizeLabel(value).replace(/\.00$/, '');
    if (looksLikeGiftPrice(normalized)) return true;
    const list = Array.isArray(labels) && labels.length ? labels : DIGITAL_GIFT_LABELS;
    return list.some((label) => label === normalized || label === `$${normalized.replace(/^\$/, '')}`);
  }

  function normalizeGift(value) {
    const normalized = normalizeLabel(value).replace(/\.00$/, '');
    const match = DIGITAL_GIFT_LABELS.find((label) => label === normalized || label === `$${normalized.replace(/^\$/, '')}`);
    return match || normalized;
  }

  function isHardcoverFormat(value) {
    return /^Hardcover \+ PDF(?: \+ Audiobook)?$/i.test(normalizeLabel(value));
  }

  function isDigitalFormat(value) {
    const label = normalizeLabel(value);
    return /^Digital PDF(?: \+ Audiobook)?$/i.test(label);
  }

  function optionName(option) {
    return typeof option === 'string' ? option : option?.name;
  }

  function variantOptionValue(variant, index) {
    return variant?.options?.[index] ?? variant?.[`option${index + 1}`];
  }

  function variantPriceCents(variant) {
    if (!variant) return null;
    if (typeof variant.price_cents === 'number' && Number.isFinite(variant.price_cents)) {
      return Math.round(variant.price_cents);
    }
    if (typeof variant.price === 'number' && Number.isFinite(variant.price)) {
      return Math.round(variant.price);
    }
    if (typeof variant.price === 'string' && variant.price.trim()) {
      const numeric = variant.price.replace(/[^0-9.]/g, '');
      if (!numeric) return null;
      return Math.round(Number(numeric) * 100);
    }
    return null;
  }

  function formatUsdFromCents(cents) {
    if (cents == null || !Number.isFinite(cents)) return '';
    const dollars = cents / 100;
    if (Number.isInteger(dollars)) return `$${dollars}`;
    return `$${dollars.toFixed(2)}`;
  }

  function isBookPriceTruthProduct(product) {
    const names = (product?.options || []).map(optionName).filter(Boolean);
    const hasPrice = names.some((name) => name.toLowerCase() === PRICE_OPTION.toLowerCase());
    const hasFormat = names.some((name) => name.toLowerCase() === FORMAT_OPTION.toLowerCase());
    return Boolean(hasPrice && hasFormat && product?.variants?.length);
  }

  function namedVariantOption(product, variant, name) {
    const index = (product?.options || []).findIndex((option) => optionName(option)?.toLowerCase() === name.toLowerCase());
    return index < 0 ? null : variantOptionValue(variant, index);
  }

  function variantFormat(variant, product) {
    if (!variant) return '';
    const named = namedVariantOption(product, variant, FORMAT_OPTION);
    if (named != null) return normalizeLabel(named);
    const values = variant.options || [variant.option1, variant.option2, variant.option3];
    const formats = values.filter((value) => isHardcoverFormat(value) || isDigitalFormat(value));
    if (formats.length === 1) return normalizeLabel(formats[0]);
    if (formats.length > 1) return '';
    const titleParts = normalizeLabel(variant.title).split(' / ');
    const last = titleParts[titleParts.length - 1];
    return isHardcoverFormat(last) || isDigitalFormat(last) ? last : '';
  }

  function variantGift(variant, product) {
    return namedVariantOption(product, variant, PRICE_OPTION) ?? variant?.option1;
  }

  function isHardcoverVariant(variant, product) {
    return isHardcoverFormat(variantFormat(variant, product));
  }

  function isDigitalVariant(variant, product) {
    return isDigitalFormat(variantFormat(variant, product));
  }

  function digitalGiftVariants(product, format) {
    return (product?.variants || []).filter((variant) => isDigitalVariant(variant, product) && looksLikeGiftPrice(variantGift(variant, product)) && (!format || variantFormat(variant, product) === format));
  }

  function hardcoverVariants(product, format) {
    return (product?.variants || []).filter((variant) => isHardcoverVariant(variant, product) && (!format || variantFormat(variant, product) === format));
  }

  function available(variant) {
    return variant && variant.available !== false;
  }

  function canonicalHardcoverVariant(product, format) {
    const candidates = hardcoverVariants(product, format).filter(available);
    if (candidates.length === 0) return null;
    const truthful = candidates.filter((variant) => !isGiftTierLabel(variantGift(variant, product)));
    const pool = truthful.length > 0 ? truthful : candidates;
    return pool.slice().sort((a, b) => {
      const priceDelta = (variantPriceCents(a) ?? Number.POSITIVE_INFINITY) - (variantPriceCents(b) ?? Number.POSITIVE_INFINITY);
      if (priceDelta !== 0) return priceDelta;
      return String(a.id).localeCompare(String(b.id));
    })[0];
  }

  function findVariantById(product, id) {
    if (id == null || id === '') return null;
    const wanted = String(id);
    return (product?.variants || []).find((variant) => String(variant.id) === wanted) || null;
  }

  function hardcoverPresentation(variant, product) {
    const price = formatUsdFromCents(variantPriceCents(variant));
    const format = variantFormat(variant, product);
    return price ? `${format} — ${price}` : format;
  }

  function hardcoverCartOptions(variant, product) {
    const format = variantFormat(variant, product);
    return [{ name: FORMAT_OPTION, value: format }];
  }

  function cartLabelFromOptions(options) {
    return (options || []).map((option) => option.value).join(', ');
  }

  /**
   * Founder-forbidden: a digital gift tier appears on a hardcover line/selection.
   * The live defect is `$25, Hardcover + PDF` while charging `$70`.
   */
  function isForbiddenHardcoverGiftState(state) {
    if (!state) return false;
    const option1 = state.option1 || state.gift || '';
    const option2 = state.option2 || state.format || '';
    const title = state.title || state.variantTitle || '';
    const cartOptions = state.cartOptions || [];
    const cartLabel =
      state.cartLabel ||
      cartLabelFromOptions(cartOptions) ||
      title;
    const hardcover =
      isHardcoverFormat(option2) ||
      isHardcoverVariant({ title }) ||
      isHardcoverVariant({ title: cartLabel.replace(', ', ' / ') }) ||
      cartOptions.some((option) => isHardcoverFormat(option.value) || /format/i.test(option.name || '') && isHardcoverFormat(option.value));
    if (!hardcover) return false;
    if (looksLikeGiftPrice(option1) || isGiftTierLabel(option1)) return true;
    if (looksLikeGiftPrice(cartLabel) || DIGITAL_GIFT_LABELS.some((label) => cartLabel.includes(label) || title.includes(label))) {
      return true;
    }
    if (cartOptions.some((option) => looksLikeGiftPrice(option.value) || isGiftTierLabel(option.value) || /your price/i.test(option.name || ''))) {
      return true;
    }
    return false;
  }

  function formatCanonicalDate(canonical) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizeLabel(canonical));
    if (!match) return normalizeLabel(canonical);
    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  function formatCanonicalTime(canonical) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(normalizeLabel(canonical));
    if (!match) return normalizeLabel(canonical);
    const hour24 = Number(match[1]);
    const minute = match[2];
    const suffix = hour24 >= 12 && hour24 <= 23 ? 'PM' : 'AM';
    let hour12 = hour24 % 12;
    if (hour12 === 0) hour12 = 12;
    return `${hour12}:${minute} ${suffix}`;
  }

  function canonicalDateFromDisplayOrCanonical(value) {
    const trimmed = normalizeLabel(value);
    const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
    if (!us) return null;
    return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }

  function canonicalTimeFromDisplayOrCanonical(value) {
    const trimmed = normalizeLabel(value);
    const twentyFour = /^(\d{1,2}):(\d{2})$/.exec(trimmed);
    if (twentyFour) return `${String(Number(twentyFour[1])).padStart(2, '0')}:${twentyFour[2]}`;
    const twelve = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(trimmed);
    if (!twelve) return null;
    let hour = Number(twelve[1]) % 12;
    if (/pm/i.test(twelve[3])) hour += 12;
    return `${String(hour).padStart(2, '0')}:${twelve[2]}`;
  }

  function roundTripDateTime(canonicalDate, canonicalTime) {
    const displayDate = formatCanonicalDate(canonicalDate);
    const displayTime = formatCanonicalTime(canonicalTime);
    return {
      canonicalDate: canonicalDateFromDisplayOrCanonical(canonicalDate),
      canonicalTime: canonicalTimeFromDisplayOrCanonical(canonicalTime),
      displayDate,
      displayTime,
      displayDoesNotChangeCanonical:
        canonicalDateFromDisplayOrCanonical(canonicalDate) === canonicalDate &&
        canonicalTimeFromDisplayOrCanonical(canonicalTime) === canonicalTime,
    };
  }

  function formatCartProperty(name, value) {
    const key = normalizeLabel(name).toLowerCase();
    const raw = normalizeLabel(value);
    if (/(birth date)$/.test(key)) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatCanonicalDate(raw);
      return raw;
    }
    if (/(birth time)$/.test(key)) {
      if (/^\d{1,2}:\d{2}$/.test(raw) && !/am|pm/i.test(raw)) return formatCanonicalTime(raw);
      return raw;
    }
    return value;
  }

  function checkoutVisiblePropertyContract() {
    return DUAL_PROPERTY_CONTRACT;
  }

  function selectedRecord(selected) {
    if (!selected) return {};
    if (selected instanceof Map) return Object.fromEntries(selected.entries());
    return { ...selected };
  }

  function resolveBookSelection(product, selected, ctx = {}) {
    if (!isBookPriceTruthProduct(product)) {
      return { applicable: false };
    }

    const record = selectedRecord(selected);
    const urlVariant = findVariantById(product, ctx.urlVariantId);
    const selectedFormat = record[FORMAT_OPTION];
    const selectedGift = record[PRICE_OPTION];
    const format = selectedFormat || variantFormat(urlVariant, product) || variantFormat(digitalGiftVariants(product)[0], product);
    if (!isDigitalFormat(format) && !isHardcoverFormat(format)) return { applicable: true, error: 'unsupported-format' };
    const gift = selectedGift || variantGift(urlVariant, product) || '';
    const sourceVariant =
      urlVariant ||
      (product.variants || []).find((variant) => {
        return (
          (!gift || variantGift(variant, product) === gift) &&
          (!format || variantFormat(variant, product) === format)
        );
      }) ||
      null;

    const wantsHardcover = isHardcoverFormat(format);

    if (wantsHardcover) {
      const canonical = canonicalHardcoverVariant(product, format);
      if (!canonical) {
        return { applicable: true, kind: 'hardcover', error: 'no-hardcover-variant' };
      }
      const unmapped = sourceVariant && isHardcoverVariant(sourceVariant, product) ? sourceVariant : {
        option1: gift,
        option2: format,
        title: `${gift} / ${format}`,
        price: sourceVariant?.price,
        price_cents: sourceVariant?.price_cents,
      };
      return {
        applicable: true,
        kind: 'hardcover',
        variant: canonical,
        giftTiersVisible: false,
        giftTiersSelectable: false,
        giftLabels: [],
        presentationLabel: hardcoverPresentation(canonical, product),
        displayPrice: formatUsdFromCents(variantPriceCents(canonical)),
        displayPriceCents: variantPriceCents(canonical),
        cartOptions: hardcoverCartOptions(canonical, product),
        cartLabel: hardcoverPresentation(canonical, product),
        addToCartVariantId: String(canonical.id),
        urlVariantId: String(canonical.id),
        acceleratedCheckoutVariantId: String(canonical.id),
        restoredDigitalGift: null,
        remappedFromVariantId: sourceVariant && String(sourceVariant.id) !== String(canonical.id) ? String(sourceVariant.id) : null,
        forbiddenIfUnmapped: isForbiddenHardcoverGiftState(unmapped),
      };
    }

    const digitals = digitalGiftVariants(product, format);
    const wantedGift = gift && isGiftTierLabel(gift) ? normalizeGift(gift) : null;
    const lastGift = ctx.lastDigitalGift && isGiftTierLabel(ctx.lastDigitalGift) ? normalizeGift(ctx.lastDigitalGift) : null;
    const pickGift = wantedGift || lastGift || (digitals[0] ? normalizeGift(variantGift(digitals[0], product)) : null);
    const variant =
      digitals.find((row) => normalizeGift(variantGift(row, product)) === pickGift) ||
      digitals[0] ||
      null;
    if (!variant) {
      return { applicable: true, kind: 'digital', error: 'no-digital-variant' };
    }
    const giftLabel = normalizeGift(variantGift(variant, product));
    const actualFormat = variantFormat(variant, product);
    return {
      applicable: true,
      kind: 'digital',
      variant,
      giftTiersVisible: true,
      giftTiersSelectable: true,
      giftLabels: DIGITAL_GIFT_LABELS.slice(),
      presentationLabel: `${giftLabel} / ${actualFormat}`,
      displayPrice: formatUsdFromCents(variantPriceCents(variant)),
      displayPriceCents: variantPriceCents(variant),
      cartOptions: [
        { name: PRICE_OPTION, value: giftLabel },
        { name: FORMAT_OPTION, value: actualFormat },
      ],
      cartLabel: `${giftLabel}, ${actualFormat}`,
      addToCartVariantId: String(variant.id),
      urlVariantId: String(variant.id),
      acceleratedCheckoutVariantId: String(variant.id),
      restoredDigitalGift: giftLabel,
      remappedFromVariantId: null,
      forbiddenIfUnmapped: false,
    };
  }

  function cartLinePresentation(line) {
    const options = Array.isArray(line?.options) ? line.options : [];
    const hardcover = options.some((option) => isHardcoverFormat(option.value)) || isHardcoverVariant({ title: line?.variantTitle || line?.title });
    if (!hardcover) {
      return {
        options,
        label: cartLabelFromOptions(options) || line?.title || '',
        skippedGiftTier: false,
      };
    }
    const filtered = options.filter((option) => {
      if (/your price/i.test(option.name || '')) return false;
      if (isGiftTierLabel(option.value)) return false;
      return true;
    });
    const variant = {
      option1: line?.option1,
      option2: options.find((option) => option.name === FORMAT_OPTION)?.value || line?.option2,
      title: line?.variantTitle || line?.title,
      price: line?.price,
      price_cents: line?.price_cents,
    };
    const label = hardcoverPresentation(variant) || cartLabelFromOptions(filtered);
    return {
      options: filtered.length > 0 ? filtered : hardcoverCartOptions(variant),
      label,
      skippedGiftTier: filtered.length !== options.length,
    };
  }

  function cssOnlyHideWouldStillBeForbidden(state) {
    return isForbiddenHardcoverGiftState({
      ...state,
      giftTiersVisible: false,
    });
  }

  function getSelectedOptionsFromPicker(picker) {
    const selected = {};
    if (!picker) return selected;
    for (const select of picker.querySelectorAll('select')) {
      const option = select.selectedOptions[0];
      if (option?.dataset.optionName) selected[option.dataset.optionName] = option.value;
    }
    for (const input of picker.querySelectorAll('fieldset input:checked')) {
      if (input.dataset.optionName) selected[input.dataset.optionName] = input.value;
    }
    return selected;
  }

  function setControlValue(picker, optionNameValue, value) {
    const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find((input) => {
      return input.dataset.optionName === optionNameValue && input.value === value;
    });
    if (radio) {
      radio.checked = true;
      radio.dataset.currentChecked = 'true';
      return radio;
    }
    const select = Array.from(picker.querySelectorAll('select')).find((element) => {
      return element.selectedOptions[0]?.dataset.optionName === optionNameValue || element.name === `options[${optionNameValue}]`;
    });
    if (select) {
      select.value = value;
      for (const option of select.options) {
        if (option.value === value) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      }
      return select;
    }
    return null;
  }

  function setGiftControlsEnabled(picker, enabled) {
    const giftFieldsets = picker.querySelectorAll('[data-stz-option-role="gift-price"]');
    for (const fieldset of giftFieldsets) {
      fieldset.classList.toggle('stz-book-price-truth__gift-hidden', !enabled);
      fieldset.hidden = !enabled;
      fieldset.setAttribute('aria-hidden', enabled ? 'false' : 'true');
      for (const input of fieldset.querySelectorAll('input, select')) {
        input.disabled = !enabled;
        if (!enabled) {
          input.setAttribute('data-stz-gift-suppressed', 'true');
        } else {
          input.removeAttribute('data-stz-gift-suppressed');
        }
      }
    }
    const priceNote = picker.querySelector('[data-stz-hardcover-price]');
    if (priceNote) {
      priceNote.hidden = enabled;
      priceNote.setAttribute('aria-hidden', enabled ? 'true' : 'false');
    }
  }

  function updateHardcoverPriceNote(picker, resolved) {
    const valueNode = picker.querySelector('[data-stz-hardcover-price-value]');
    const note = picker.querySelector('[data-stz-hardcover-price]');
    if (valueNode && resolved.displayPrice) valueNode.textContent = resolved.displayPrice;
    if (note && resolved.presentationLabel) {
      const labelNode = note.querySelector('[data-stz-hardcover-price-label]');
      if (labelNode) labelNode.textContent = resolved.presentationLabel;
    }
  }

  function syncFormAndUrl(picker, resolved) {
    if (!resolved?.variant) return;
    const id = String(resolved.variant.id);
    const productId = picker.dataset.productId;
    for (const form of document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`)) {
      const input = form.querySelector('input[name="id"]');
      if (input && input.value !== id) {
        input.value = id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      form.dataset.stzBookResolvedVariantId = id;
    }
    if (picker.dataset.templateProductMatch === 'true') {
      const url = new URL(window.location.href);
      if (url.searchParams.get('variant') !== id) {
        url.searchParams.set('variant', id);
        if (url.href !== window.location.href) {
          history.replaceState({}, '', url.toString());
        }
      }
    }
  }

  async function getProductJson(picker) {
    if (picker.__stzProductJson) return picker.__stzProductJson;
    if (!picker.dataset.productUrl) return null;
    picker.__stzProductJsonPromise ??= fetch(`${picker.dataset.productUrl.split('?')[0]}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        picker.__stzProductJson = json;
        return json;
      })
      .catch((error) => {
        console.warn('[starlyzed-book-price-truth] Could not load product JSON:', error);
        return null;
      });
    return picker.__stzProductJsonPromise;
  }

  function applyResolvedControls(picker, product, resolved) {
    if (!resolved?.variant) return;
    const variant = resolved.variant;
    // The format-first ladder picker (variant-main-picker.liquid, data-stz-format-ladder)
    // renders the hardcover price row itself on every server re-render, so the legacy
    // hide/disable + price note must stay out of its way.
    const formatLadder = picker.dataset.stzFormatLadder === 'true';
    if (resolved.kind === 'hardcover') {
      if (!formatLadder) setGiftControlsEnabled(picker, false);
      setControlValue(picker, FORMAT_OPTION, variantFormat(variant, product));
      if (variantGift(variant, product)) setControlValue(picker, PRICE_OPTION, variantGift(variant, product));
      if (!formatLadder) updateHardcoverPriceNote(picker, resolved);
    } else {
      if (!formatLadder) setGiftControlsEnabled(picker, true);
      setControlValue(picker, PRICE_OPTION, variantGift(variant, product));
      setControlValue(picker, FORMAT_OPTION, variantFormat(variant, product));
      rememberDigitalGift(product, variantGift(variant, product));
    }
    picker.dataset.stzBookResolvedVariantId = String(variant.id);
    picker.dataset.stzBookResolvedKind = resolved.kind;
  }

  function rememberDigitalGift(product, gift) {
    if (product?.id && isGiftTierLabel(gift)) {
      lastDigitalGiftByProductId.set(String(product.id), normalizeGift(gift));
    }
  }

  async function syncPicker(picker, { dispatchIfRemapped = false } = {}) {
    if (!picker || picker.dataset.stzBookPriceTruth !== 'true') return null;
    const product = await getProductJson(picker);
    if (!isBookPriceTruthProduct(product)) return null;
    const selected = getSelectedOptionsFromPicker(picker);
    const urlVariantId = new URL(window.location.href).searchParams.get('variant');
    const lastDigitalGift = lastDigitalGiftByProductId.get(String(product.id));
    if (selected[PRICE_OPTION] && selected[FORMAT_OPTION] && isDigitalFormat(selected[FORMAT_OPTION])) {
      rememberDigitalGift(product, selected[PRICE_OPTION]);
    }
    const resolved = resolveBookSelection(product, selected, { urlVariantId, lastDigitalGift });
    if (!resolved?.applicable || !resolved.variant) return resolved;
    const beforeId = urlVariantId || picker.dataset.stzBookResolvedVariantId || '';
    applyResolvedControls(picker, product, resolved);
    syncFormAndUrl(picker, resolved);
    if (dispatchIfRemapped && String(beforeId) && String(beforeId) !== String(resolved.variant.id)) {
      const formatInput =
        picker.querySelector(`[data-option-name="${FORMAT_OPTION}"]:checked`) ||
        picker.querySelector(`[data-option-name="${FORMAT_OPTION}"]`);
      if (formatInput) {
        const replay = new Event('change', { bubbles: true });
        replay[SYNC_FLAG] = true;
        formatInput.dispatchEvent(replay);
      }
    }
    return resolved;
  }

  function truthfulFormVariantId(formComponent, currentId) {
    const productId = formComponent?.dataset?.productId;
    if (!productId) return currentId;
    const picker = document.querySelector(`variant-picker[data-product-id="${productId}"][data-stz-book-price-truth="true"]`);
    if (!picker) return currentId;
    const product = picker.__stzProductJson;
    if (!isBookPriceTruthProduct(product)) return currentId;
    const selected = getSelectedOptionsFromPicker(picker);
    const resolved = resolveBookSelection(product, selected, {
      urlVariantId: currentId || new URL(window.location.href).searchParams.get('variant'),
      lastDigitalGift: lastDigitalGiftByProductId.get(String(product.id)),
    });
    return resolved?.addToCartVariantId || currentId;
  }

  function repairProductSelection(product, selectedOptions, changedTarget) {
    if (!isBookPriceTruthProduct(product)) return null;
    const selected = selectedOptions instanceof Map ? Object.fromEntries(selectedOptions) : selectedRecord(selectedOptions);
    if (changedTarget?.dataset?.optionName === FORMAT_OPTION && isDigitalFormat(changedTarget.value || changedTarget.dataset?.value)) {
      selected[FORMAT_OPTION] = changedTarget.value || changedTarget.dataset?.value;
    }
    if (changedTarget?.dataset?.optionName === FORMAT_OPTION && isHardcoverFormat(changedTarget.value || changedTarget.dataset?.value)) {
      selected[FORMAT_OPTION] = changedTarget.value || changedTarget.dataset?.value;
    }
    const resolved = resolveBookSelection(product, selected, {
      lastDigitalGift: lastDigitalGiftByProductId.get(String(product.id)),
    });
    return resolved?.variant || null;
  }

  async function onPickerChange(event) {
    if (event[SYNC_FLAG]) return;
    if (!(event.target instanceof HTMLElement)) return;
    const picker = event.target.closest('variant-picker');
    if (!picker || picker.dataset.stzBookPriceTruth !== 'true') return;
    if (event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;
    await syncPicker(picker);
  }

  function install() {
    if (install.done || typeof document === 'undefined') return;
    install.done = true;
    document.addEventListener('change', onPickerChange, true);
    const run = () => {
      for (const picker of document.querySelectorAll('variant-picker[data-stz-book-price-truth="true"]')) {
        syncPicker(picker, { dispatchIfRemapped: true });
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  return {
    DIGITAL_GIFT_LABELS,
    PUBLICATION_CENSUS_HANDLES,
    DUAL_PROPERTY_CONTRACT,
    PRICE_OPTION,
    FORMAT_OPTION,
    DIGITAL_FORMAT,
    HARDCOVER_FORMAT,
    looksLikeGiftPrice,
    isGiftTierLabel,
    formatCanonicalDate,
    formatCanonicalTime,
    canonicalDateFromDisplayOrCanonical,
    canonicalTimeFromDisplayOrCanonical,
    roundTripDateTime,
    formatCartProperty,
    checkoutVisiblePropertyContract,
    isHardcoverFormat,
    isDigitalFormat,
    isBookPriceTruthProduct,
    isHardcoverVariant,
    isForbiddenHardcoverGiftState,
    cssOnlyHideWouldStillBeForbidden,
    variantPriceCents,
    formatUsdFromCents,
    digitalGiftVariants,
    hardcoverVariants,
    canonicalHardcoverVariant,
    resolveBookSelection,
    cartLinePresentation,
    repairProductSelection,
    truthfulFormVariantId,
    syncPicker,
    install,
  };
});

if (typeof document !== 'undefined') {
  const api = typeof globalThis !== 'undefined' ? globalThis.StarlyzedBookPriceTruth : null;
  api?.install?.();
}
